import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR =
    'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Traps focus inside a container element while isActive is true.
 * Container must have tabIndex={-1}.
 */
export const useFocusTrap = (containerRef: RefObject<HTMLElement>, isActive: boolean) => {
    useEffect(() => {
        const container = containerRef.current;
        if (!isActive || !container) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
            if (focusableElements.length === 0) {
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === container)) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        container.addEventListener('keydown', handleKeyDown);
        return () => container.removeEventListener('keydown', handleKeyDown);
    }, [containerRef, isActive]);
};
